import React, { useState } from 'react';
import { StackShim as Stack } from '@fluentui/react-migration-v8-v9';
import { IPersonaProps } from '@fluentui/react';
import { AddRegular, DismissRegular } from '@fluentui/react-icons';
import {
  Button,
  Field,
  FluentProvider,
  Input,
  webLightTheme,
  makeStyles,
} from '@fluentui/react-components';
import { Controller, useFieldArray } from 'react-hook-form';
import searchForUsers from '../../../utils/microsoftGraphApi/userSearch';
import { ICustomContactGroup, IServiceContactsUserAssignment } from './interfaces';
import ErrorHandlingPeoplePicker from '../../common/formFields/ErrorHandlingPeoplePicker';
import validateCustomContactPeoplePicker from './customContactValidator';
import { iconButtonStyles } from '../../../app/common/styles/CommonStyleObjects';

interface IContactGroupsFormProps {
  mainContacts: IServiceContactsUserAssignment;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: any;
}

const useStyles = makeStyles({
  stackCard: { position: 'relative', marginTop: '10px', padding: '15px' },
});

const ContactGroupsForm: React.FunctionComponent<IContactGroupsFormProps> = ({
  mainContacts,
  form,
}: IContactGroupsFormProps): JSX.Element => {
  const PICKER_RESOLVE_DELAY = 450;
  const [newGroupId, setNewGroupId] = useState(-2);

  const onResolveSuggestions = (filterText: string): IPersonaProps[] | Promise<IPersonaProps[]> => {
    if (filterText) {
      return searchForUsers(filterText);
    }
    return [];
  };

  const { errors, control, trigger, register, getValues, setValue, formState } = form;

  const { fields, remove, insert } = useFieldArray({
    control,
    name: 'customContactGroups',
  });

  const validateLabel = (newVal: string): string => {
    const allContactGroupLabels: string[] = getValues().customContactGroups.map(
      (val: ICustomContactGroup) => val.label,
    );

    if (allContactGroupLabels.filter((label: string) => label === newVal).length > 1) {
      return 'Contact Group Names must be unique';
    }
    return null;
  };

  const createCustomFinancialContactComponent = (): void => {
    insert(0, { id: newGroupId, label: 'New Label', contacts: [] });
    setNewGroupId(newGroupId - 1);
  };

  const buttonStyles: React.CSSProperties = {
    position: 'absolute',
    right: 0,
    zIndex: 0.5,
    top: 0,
  };

  const classes = useStyles();

  return (
    <FluentProvider theme={webLightTheme}>
      <Stack tokens={{ childrenGap: 30 }}>
        <Button
          appearance="primary"
          icon={<AddRegular />}
          style={{ marginBottom: 15, width: '100%' }}
          onClick={createCustomFinancialContactComponent}
        >
          Add New Contact Group
        </Button>
        {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (fields as any).map((group: ICustomContactGroup, index: number) => {
            const fieldName = `customContactGroups[${index}]`;
            return (
              <Stack key={group.id} className={`${classes.stackCard} ms-depth-8`}>
                <Button
                  appearance="subtle"
                  style={buttonStyles}
                  icon={<DismissRegular color={iconButtonStyles.color} />}
                  title={`Remove ${group.label}`}
                  aria-label={`Remove ${group.label}`}
                  onClick={(): void => {
                    remove(index);
                  }}
                />
                {/* This is used to include the custom contact group id within the payload */}
                <input
                  type="hidden"
                  ref={register()}
                  name={`${fieldName}.id`}
                  defaultValue={group.id}
                />
                <Controller
                  control={control}
                  name={`${fieldName}.label`}
                  defaultValue={group.label}
                  render={(): React.ReactElement => (
                    <Field
                      label="Contact Group Name"
                      validationMessage={errors?.customContactGroups?.[index]?.label?.message}
                    >
                      <Input
                        name={`${fieldName}.label`}
                        onBlur={(): void => {
                          if (formState.isSubmitted) {
                            trigger();
                          }
                        }}
                        onChange={(e, data): void => {
                          setValue(`${fieldName}.label`, data.value?.trim());
                        }}
                        defaultValue={group.label}
                      />
                    </Field>
                  )}
                  rules={{
                    minLength: {
                      value: 2,
                      message: 'Contact Group Names must be at least two characters long',
                    },
                    required: 'Contact Group Name is required',
                    validate: validateLabel,
                  }}
                />
                <Controller
                  name={`${fieldName}.contacts`}
                  control={control}
                  defaultValue={group.contacts}
                  render={(): React.ReactElement => (
                    <ErrorHandlingPeoplePicker
                      controlName={`${group.label}Picker${group.id}`}
                      labelText="Contacts"
                      onChange={(items?: IPersonaProps[]): void => {
                        setValue(`${fieldName}.contacts`, items);
                        // We need to re-trigger all validation since our rules are cross-input dependent
                        trigger();
                      }}
                      errorMessage={errors?.customContactGroups?.[index]?.contacts?.message}
                      removeButtonAriaLabel="Remove Contact"
                      defaultSelectedItems={group.contacts}
                      selectedItems={getValues(`${fieldName}.contacts`)}
                      onResolveSuggestions={onResolveSuggestions}
                      resolveDelay={PICKER_RESOLVE_DELAY}
                    />
                  )}
                  rules={{
                    validate: (): string => {
                      return validateCustomContactPeoplePicker({
                        updatingContactGroup: getValues(`${fieldName}`),
                        contactGroups: getValues().customContactGroups.filter(
                          (cg: ICustomContactGroup) => +cg.id !== +group.id,
                        ),
                        mainContacts,
                      });
                    },
                  }}
                />
              </Stack>
            );
          })
        }
      </Stack>
    </FluentProvider>
  );
};

export default ContactGroupsForm;
